import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { Suspense, lazy, useEffect } from 'react'
import { AuthProvider, useAuth } from './auth/AuthContext'
import { ToastProvider } from './components/Toast'
import LandingPage from './pages/LandingPage'
import PrivacyPage from './pages/PrivacyPage'
import TermsPage from './pages/TermsPage'
import PartnerTermsPage from './pages/PartnerTermsPage'
import BannedPage from './pages/BannedPage'
import PartnerProgramPage from './pages/PartnerProgramPage'
import NotFoundPage from './pages/NotFoundPage'
import CookieNotice from './components/CookieNotice'
import BillingMetrikaTracker from './components/BillingMetrikaTracker'
import { BillingFlowProvider } from './billing/BillingFlowContext'
import YandexMetrikaBootstrap from './components/YandexMetrikaBootstrap'
import ServiceIncidentBanner from './components/ServiceIncidentBanner'

const LoginPage = lazy(() => import('./pages/LoginPage'))
const ThankYouPage = lazy(() => import('./pages/ThankYouPage'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'))
const StudioPage = lazy(() => import('./pages/StudioPage'))
const ToolDeprecatedPage = lazy(() => import('./pages/ToolDeprecatedPage'))
const JobDetailPage = lazy(() => import('./pages/JobDetailPage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))
const TopUpPage = lazy(() => import('./pages/TopUpPage'))
const AdminPartnersPage = lazy(() => import('./pages/AdminPartnersPage'))

function Protected({ children }: { children: JSX.Element }) {
  const { isAuthed, hydrated, profile } = useAuth()
  const loc = useLocation()
  if (!hydrated) return null
  if (profile?.isBanned) {
    return <Navigate to="/banned" replace />
  }
  if (!isAuthed) return <Navigate to="/" state={{ returnTo: loc.pathname + loc.search + loc.hash }} replace />
  return children
}

export default function App() {
  const { search } = useLocation()
  useEffect(() => {
    const p = new URLSearchParams(search)
    const promo = p.get('promo')
    const partnerCode = p.get('partnerCode')
    const pid = p.get('partnerId')
    const hasPartnerAttribution = Boolean(promo || partnerCode || pid)

    const referralCode = p.get('referralCode') || (!hasPartnerAttribution ? p.get('ref') : null)
    const referrerId = p.get('referrerId')
    const hasReferralAttribution = Boolean(referralCode || referrerId)

    if (hasPartnerAttribution) {
      localStorage.removeItem('referral_code')
      localStorage.removeItem('referrer_id')
    }
    if (hasReferralAttribution && !hasPartnerAttribution) {
      localStorage.removeItem('promo_code')
      localStorage.removeItem('partner_id')
    }

    if (referralCode) localStorage.setItem('referral_code', referralCode)
    if (referrerId) localStorage.setItem('referrer_id', referrerId)
    if (promo) localStorage.setItem('promo_code', promo)
    if (pid) localStorage.setItem('partner_id', pid)
  }, [search])

  return (
    <AuthProvider>
      <ToastProvider>
        <BillingFlowProvider>
          <YandexMetrikaBootstrap />
          <BillingMetrikaTracker />
          <ServiceIncidentBanner />
          <Suspense fallback={null}>
            <Routes>
              <Route path="/auth" element={<LoginPage />} />
              <Route path="/" element={<LandingGate />} />
              <Route path="/thanks" element={<ThankYouPage />} />
              <Route path="/legal/privacy" element={<PrivacyPage />} />
              <Route path="/legal/terms" element={<TermsPage />} />
              <Route path="/legal/partner-terms" element={<PartnerTermsPage />} />
              <Route path="/partners" element={<PartnerProgramPage />} />
              <Route path="/banned" element={<BannedPage />} />

            <Route
              path="/dashboard"
              element={
                <Protected>
                  <DashboardPage />
                </Protected>
              }
            />
            <Route
              path="/projects"
              element={
                <Protected>
                  <ProjectsPage />
                </Protected>
              }
            />
            <Route
              path="/tool/:pipelineId"
              element={
                <Protected>
                  <ToolDeprecatedPage />
                </Protected>
              }
            />
            <Route
              path="/studio"
              element={
                <Protected>
                  <StudioPage />
                </Protected>
              }
            />
            <Route path="/studio/:pipelineId" element={<StudioLegacyRedirect />} />
            <Route path="/jobs" element={<Navigate to="/profile?tab=jobs" replace />} />
            <Route
              path="/jobs/:id"
              element={
                <Protected>
                  <JobDetailPage />
                </Protected>
              }
            />
            <Route
              path="/profile"
              element={
                <Protected>
                  <ProfilePage />
                </Protected>
              }
            />
            <Route
              path="/topup"
              element={
                <Protected>
                  <TopUpPage />
                </Protected>
              }
            />

            <Route path="/admin/partners" element={
              <Protected>
                <AdminPartnersPage />
              </Protected>
            } />

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
          <CookieNotice />
        </BillingFlowProvider>
      </ToastProvider>
    </AuthProvider>
  )
}

function LandingGate() {
  const { isAuthed } = useAuth()
  if (isAuthed) return <Navigate to="/dashboard" replace />
  return <LandingPage />
}

function StudioLegacyRedirect() {
  const { pipelineId = '' } = useParams()
  const next = pipelineId ? `/studio?pipeline=${encodeURIComponent(pipelineId)}` : '/studio'
  return <Navigate to={next} replace />
}

